<script>
import bem from '@/utils/bem';
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';

const b = bem('edit-colors-modal');

export default {
  name: 'EditColorsModal',
  components: {
    ColorPicker,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialColors: {
      type: Object,
      default: () => ({
        color1: '',
        color2: '',
        color3: '',
      }),
    },
  },
  emits: ['close', 'save'],
  data() {
    return {
      colors: {
        color1: '',
        color2: '',
        color3: '',
      },
      activeColorKey: 'color1',
    };
  },
  computed: {
    isSaveDisabled() {
      return !this.colors.color1 || !this.colors.color2 || !this.colors.color3;
    },
    pickerColor: {
      get() {
        return this.colors[this.activeColorKey] || '#000000';
      },
      set(value) {
        this.colors[this.activeColorKey] = value;
      },
    },
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.colors = { ...this.initialColors };
          this.activeColorKey = 'color1';
        }
      },
      immediate: true,
    },
    initialColors: {
      handler(newVal) {
        if (this.visible) {
          this.colors = { ...newVal };
        }
      },
      deep: true,
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    setActiveColor(key) {
      this.activeColorKey = key;
    },
    handleHexInput(key, event) {
      let value = event.target.value.trim();
      if (value && !value.startsWith('#')) {
        value = `#${value}`;
      }
      this.colors[key] = value;
    },
    handleSave() {
      this.$emit('save', { ...this.colors });
    },
    handleCancel() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    :footer="null"
    :closable="true"
    :width="1050"
    centered
    @cancel="handleCancel"
    :class="b()"
  >
    <div :class="b('content')">
      <h2 :class="b('title')">Choose Three Colors to Refresh the Pages.</h2>
      <div :class="b('separate')" />
      <div :class="b('wrapper')">
        <div :class="b('colors-section')">
          <div
            :class="b('color-row', { active: activeColorKey === 'color1' })"
            role="button"
            tabindex="0"
            @click="setActiveColor('color1')"
            @keydown.enter="setActiveColor('color1')"
          >
            <span :class="b('color-label')">Color 1:</span>
            <input
              id="color1-input"
              :class="b('hex-input')"
              type="text"
              placeholder="Hex"
              aria-label="Color 1 hex value"
              :value="colors.color1"
              @input="handleHexInput('color1', $event)"
              @focus="setActiveColor('color1')"
            />
            <div
              :class="b('color-preview')"
              :style="{ backgroundColor: colors.color1 || '#fff' }"
            />
          </div>
          <div
            :class="b('color-row', { active: activeColorKey === 'color2' })"
            role="button"
            tabindex="0"
            @click="setActiveColor('color2')"
            @keydown.enter="setActiveColor('color2')"
          >
            <span :class="b('color-label')">Color 2:</span>
            <input
              id="color2-input"
              :class="b('hex-input')"
              type="text"
              placeholder="Hex"
              aria-label="Color 2 hex value"
              :value="colors.color2"
              @input="handleHexInput('color2', $event)"
              @focus="setActiveColor('color2')"
            />
            <div
              :class="b('color-preview')"
              :style="{ backgroundColor: colors.color2 || '#fff' }"
            />
          </div>
          <div
            :class="b('color-row', { active: activeColorKey === 'color3' })"
            role="button"
            tabindex="0"
            @click="setActiveColor('color3')"
            @keydown.enter="setActiveColor('color3')"
          >
            <span :class="b('color-label')">Color 3:</span>
            <input
              id="color3-input"
              :class="b('hex-input')"
              type="text"
              placeholder="Hex"
              aria-label="Color 3 hex value"
              :value="colors.color3"
              @input="handleHexInput('color3', $event)"
              @focus="setActiveColor('color3')"
            />
            <div
              :class="b('color-preview')"
              :style="{ backgroundColor: colors.color3 || '#fff' }"
            />
          </div>
        </div>
        <div :class="b('picker-section')">
          <div :class="b('picker-label')">
            Select color for {{ activeColorKey.replace('color', 'Color ') }}:
          </div>
          <color-picker
            v-model:pureColor="pickerColor"
            :class="b('picker')"
            :format="'hex6'"
            :disableAlpha="true"
          />
        </div>
      </div>
      <div :class="b('buttons')">
        <button
          :class="b('btn', { cancel: true })"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button
          :class="b('btn', { save: true, disabled: isSaveDisabled })"
          :disabled="isSaveDisabled"
          @click="handleSave"
        >
          Save
        </button>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.edit-colors-modal {
  padding: 0 !important;

  &__separate {
    width: 100%;
    height: 6px;
    background: #DDD;
    margin: 28px 0;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(6);
      margin: vw-full(28) 0;
    }
  }

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    font-weight: 500;
    color: #293241;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: row;
    gap: 16px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
    }
  }

  &__colors-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
      margin-bottom: vw-full(24);
    }
  }

  &__color-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    border: 2px solid transparent;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
      padding: vw-full(12) vw-full(16);
      border-radius: vw-full(12);
    }

    &:hover {
      background: #f5f5f5;
    }

    &_active {
      border-color: #242424;
      background: #f9f9f9;
    }
  }

  &__color-label {
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    font-weight: 500;
    color: #293241;
    min-width: 300px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
      min-width: vw-full(300);
    }
  }

  &__color-preview {
    width: 213px;
    height: 56px;
    border-radius: 8px;
    border: 1px solid #DDD;
    background: #DDD;
    flex-shrink: 0;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(213);
      height: vw-full(56);
      border-radius: vw-full(8);
    }
  }

  &__hex-input {
    flex: 1;
    width: 213px;
    height: 56px;
    padding: 0 16px;
    font-family: Montserrat, sans-serif;
    font-size: 28px;
    color: #242424;
    border: 1px solid #ddd;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s ease;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(213);
      height: vw-full(56);
      padding: 0 vw-full(16);
      font-size: vw-full(14);
      border-radius: vw-full(8);
    }

    &:focus {
      border-color: #242424;
    }

    &::placeholder {
      color: #999;
      text-align: center;
    }
  }

  &__picker-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 24px;
    background: #f9f9f9;
    border-radius: 12px;
    margin-bottom: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
      padding: vw-full(24);
      border-radius: vw-full(12);
      margin-bottom: vw-full(24);
    }
  }

  &__picker-label {
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #666;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;
    gap: 16px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
    }
  }

  &__btn {
    min-width: 140px;
    height: 48px;
    padding: 0 32px;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 500;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      min-width: vw-full(140);
      height: vw-full(48);
      padding: 0 vw-full(32);
      font-size: vw-full(16);
      border-radius: vw-full(12);
    }

    &_cancel {
      background: #fff;
      color: #242424;
      border: 1px solid #242424;

      &:hover {
        background: #f5f5f5;
      }
    }

    &_save {
      background: #242424;
      color: #fff;
      border: 1px solid #242424;

      &:hover {
        background: #333;
      }

      &_disabled {
        background: #ccc;
        border-color: #ccc;
        cursor: not-allowed;

        &:hover {
          background: #ccc;
        }
      }
    }
  }
}
</style>
